import { useMemo } from 'react';
import { useStore } from '@/hooks/useStore';
import { getSupplyItemsByHtx, getSupplyHistoriesBySupply } from '@/store';
import { useAuth } from '@/hooks/useAuth';
import { formatNumber } from '@/utils/format';

interface StatCardProps {
  label: string;
  value: string;
  color: 'emerald' | 'amber' | 'red' | 'slate' | 'sky' | 'lime';
  icon: string;
  sub?: string;
}

function StatCard({ label, value, color, icon, sub }: StatCardProps) {
  const colorMap: Record<string, string> = {
    emerald: 'bg-agrida-50 text-agrida-700',
    amber: 'bg-amber-50 text-amber-700',
    red: 'bg-red-50 text-red-700',
    slate: 'bg-slate-50 text-slate-700',
    sky: 'bg-sky-50 text-sky-700',
    lime: 'bg-lime-50 text-lime-700',
  };
  return (
    <div className={`rounded-2xl px-3.5 py-3 flex items-center gap-2.5 ${colorMap[color]}`}>
      <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/70 shrink-0">
        <i className={`${icon} text-sm`} />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-medium opacity-70 truncate">{label}</p>
        <p className="text-base font-bold leading-tight">{value}</p>
        {sub && <p className="text-[10px] opacity-60 mt-0.5 leading-tight">{sub}</p>}
      </div>
    </div>
  );
}

export function SupplyStats() {
  useStore();
  const { user } = useAuth();
  const items = getSupplyItemsByHtx(user?.htxId);
  const histories = useMemo(() => items.flatMap((i) => getSupplyHistoriesBySupply(i.id)), [items]);

  const stats = useMemo(() => {
    const totalItems = items.length;
    const totalStock = items.reduce((sum, i) => sum + i.stock, 0);
    const totalReserved = items.reduce((sum, i) => sum + i.reserved, 0);
    const totalAvailable = items.reduce((sum, i) => sum + i.available, 0);
    const sapHet = items.filter((i) => i.status === 'Sắp hết').length;
    const hetHang = items.filter((i) => i.status === 'Hết hàng').length;
    const ngungCungUng = items.filter((i) => i.status === 'Ngừng cung ứng').length;

    // Recent 30 days movements
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const recentHistories = histories.filter((h) => new Date(h.ngay) >= thirtyDaysAgo);
    const nhapKho = recentHistories.filter((h) => h.loai === 'Nhập').length;
    const xuatKho = recentHistories.filter((h) => h.loai === 'Xuất').length;
    const dieuChinh = recentHistories.filter((h) => h.loai === 'Điều chỉnh').length;

    return { totalItems, totalStock, totalReserved, totalAvailable, sapHet, hetHang, ngungCungUng, nhapKho, xuatKho, dieuChinh };
  }, [items, histories]);

  return (
    <div className="space-y-2 mb-4">
      {/* Row 1: Tổng quan kho */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        <StatCard label="Tổng mặt hàng" value={String(stats.totalItems)} color="slate" icon="ri-box-3-line" />
        <StatCard label="Tổng tồn kho" value={`${formatNumber(stats.totalStock)} đv`} color="emerald" icon="ri-archive-line" />
        <StatCard label="Đã cấp/giữ chỗ" value={`${formatNumber(stats.totalReserved)} đv`} color="amber" icon="ri-user-received-line" sub={`Khả dụng: ${formatNumber(stats.totalAvailable)} đv`} />
        <StatCard label="Sắp hết" value={String(stats.sapHet)} color="amber" icon="ri-error-warning-line" />
        <StatCard label="Hết / Ngừng" value={`${stats.hetHang + stats.ngungCungUng}`} color="red" icon="ri-indeterminate-circle-line" sub={stats.hetHang > 0 ? `${stats.hetHang} hết, ${stats.ngungCungUng} ngừng` : undefined} />
      </div>

      {/* Row 2: Hoạt động kho gần đây (30 ngày) */}
      <div className="grid grid-cols-3 gap-2">
        <StatCard label="Nhập kho (30 ngày)" value={`${stats.nhapKho} lần`} color="emerald" icon="ri-arrow-down-circle-line" />
        <StatCard label="Xuất kho (30 ngày)" value={`${stats.xuatKho} lần`} color="sky" icon="ri-arrow-up-circle-line" />
        <StatCard label="Điều chỉnh (30 ngày)" value={`${stats.dieuChinh} lần`} color="slate" icon="ri-settings-3-line" />
      </div>
    </div>
  );
}